import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Phone, Shield, Clock, CheckCircle, Star, MapPin, 
  DoorOpen, Key, Lock, Wrench, ChevronRight, Award,
  Users, Zap, ThumbsUp
} from "lucide-react";
import QuoteForm from "../components/QuoteForm";
import GoogleReviewsBadge from "../components/GoogleReviewsBadge";
import HeroBanner from "../components/HeroBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Static departments data - will always work regardless of database
const STATIC_DEPARTMENTS = [
  { 
    id: "1", name: "Paris", code: "75", slug: "paris-75",
    cities: ["Paris 1er", "Paris 2ème", "Paris 3ème", "Paris 4ème", "Paris 5ème", "Paris 6ème", "Paris 7ème", "Paris 8ème", "Paris 9ème", "Paris 10ème", "Paris 11ème", "Paris 12ème", "Paris 13ème", "Paris 14ème", "Paris 15ème", "Paris 16ème", "Paris 17ème", "Paris 18ème", "Paris 19ème", "Paris 20ème"]
  },
  { 
    id: "2", name: "Hauts-de-Seine", code: "92", slug: "hauts-de-seine-92",
    cities: ["Boulogne-Billancourt", "Neuilly-sur-Seine", "Levallois-Perret", "Nanterre", "Courbevoie", "Colombes", "Asnières-sur-Seine", "Rueil-Malmaison", "Issy-les-Moulineaux", "Antony", "Clamart", "Meudon", "Montrouge", "Suresnes", "Puteaux"]
  },
  { 
    id: "3", name: "Seine-Saint-Denis", code: "93", slug: "seine-saint-denis-93",
    cities: ["Saint-Denis", "Montreuil", "Bobigny", "Aubervilliers", "Pantin", "Aulnay-sous-Bois", "Drancy", "Noisy-le-Grand", "Bondy", "Épinay-sur-Seine", "Sevran", "Livry-Gargan", "Le Blanc-Mesnil", "Saint-Ouen", "Rosny-sous-Bois"]
  },
  { 
    id: "4", name: "Val-de-Marne", code: "94", slug: "val-de-marne-94",
    cities: ["Créteil", "Vincennes", "Saint-Maur-des-Fossés", "Vitry-sur-Seine", "Champigny-sur-Marne", "Ivry-sur-Seine", "Maisons-Alfort", "Fontenay-sous-Bois", "Villejuif", "Nogent-sur-Marne", "Le Perreux-sur-Marne", "Alfortville", "Charenton-le-Pont", "Joinville-le-Pont", "Choisy-le-Roi"]
  },
  { 
    id: "5", name: "Yvelines", code: "78", slug: "yvelines-78",
    cities: ["Versailles", "Saint-Germain-en-Laye", "Sartrouville", "Poissy", "Conflans-Sainte-Honorine", "Mantes-la-Jolie", "Les Mureaux", "Houilles", "Chatou", "Le Chesnay-Rocquencourt", "Montigny-le-Bretonneux", "Rambouillet", "Maisons-Laffitte", "Le Vésinet", "Plaisir"]
  },
  { 
    id: "6", name: "Val-d'Oise", code: "95", slug: "val-doise-95",
    cities: ["Argenteuil", "Cergy", "Sarcelles", "Pontoise", "Franconville", "Garges-lès-Gonesse", "Ermont", "Bezons", "Goussainville", "Herblay-sur-Seine", "Eaubonne", "Saint-Ouen-l'Aumône", "Taverny", "Villiers-le-Bel", "Montmorency"]
  },
  { 
    id: "7", name: "Essonne", code: "91", slug: "essonne-91",
    cities: ["Évry-Courcouronnes", "Corbeil-Essonnes", "Massy", "Palaiseau", "Savigny-sur-Orge", "Sainte-Geneviève-des-Bois", "Viry-Châtillon", "Athis-Mons", "Grigny", "Ris-Orangis", "Yerres", "Draveil", "Brunoy", "Les Ulis", "Longjumeau"]
  },
  { 
    id: "8", name: "Seine-et-Marne", code: "77", slug: "seine-et-marne-77",
    cities: ["Meaux", "Chelles", "Melun", "Pontault-Combault", "Savigny-le-Temple", "Torcy", "Lagny-sur-Marne", "Bussy-Saint-Georges", "Roissy-en-Brie", "Villeparisis", "Combs-la-Ville", "Ozoir-la-Ferrière", "Dammarie-les-Lys", "Le Mée-sur-Seine", "Noisiel"]
  }
];

export default function HomePage() {
  const [config, setConfig] = useState(null);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [departments, setDepartments] = useState(STATIC_DEPARTMENTS);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/config`).then(res => res.json()),
      fetch(`${API_URL}/api/services`).then(res => res.json()).catch(() => []),
      fetch(`${API_URL}/api/testimonials`).then(res => res.json()).catch(() => []),
      fetch(`${API_URL}/api/departments`).then(res => res.json()).catch(() => [])
    ])
      .then(([configData, servicesData, testimonialsData, deptsData]) => {
        setConfig(configData);
        setServices(servicesData);
        setTestimonials(testimonialsData);
        // Use API data only if it has data, otherwise keep static
        if (deptsData && deptsData.length > 0) {
          setDepartments(deptsData);
        }
      })
      .catch(console.error);
  }, []);

  const getServiceIcon = (iconName) => {
    const icons = {
      DoorOpen: DoorOpen,
      Lock: Lock,
      Key: Key,
      KeyRound: Key,
      Wrench: Wrench,
      Cylinder: Lock,
      ShieldAlert: Shield
    };
    const IconComponent = icons[iconName] || Lock;
    return <IconComponent className="w-8 h-8" />;
  };

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section with Van Image */}
      <HeroBanner
        title="DÉPANNAGE DE SERRURERIE Fichet"
        subtitle="Intervention rapide sur toutes les serrures et portes équipées de systèmes Fichet. Techniciens qualifiés en Île-de-France."
        highlightText="Fichet"
        showFeatures={true}
        showCTA={true}
        config={config}
      />

      {/* Quick CTA Section */}
      <section className="bg-gradient-to-r from-orange-500 to-orange-600 py-6">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold">Besoin d'un serrurier Fichet ?</h2>
              <p className="text-white/90">Intervention rapide en Île-de-France - Devis gratuit</p>
            </div>
            {config && (
              <a
                href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                className="flex items-center gap-3 bg-white text-[#0f2744] px-8 py-4 rounded-xl font-bold text-2xl hover:bg-gray-100 transition-colors shadow-lg"
                data-testid="quick-cta-phone"
              >
                <Phone className="w-7 h-7" />
                {config.phone}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-white py-8 border-b border-gray-100">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12">
            {/* Google Reviews Badge */}
            <GoogleReviewsBadge />
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                  <Zap className="w-6 h-6 text-blue-600" />
                </div>
                <span className="font-bold text-2xl text-[#0f2744]">30 min</span>
                <span className="text-gray-500 text-sm">Délai moyen</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
                  <ThumbsUp className="w-6 h-6 text-green-600" />
                </div>
                <span className="font-bold text-2xl text-[#0f2744]">95%</span>
                <span className="text-gray-500 text-sm">Sans dégât</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-2">
                  <Award className="w-6 h-6 text-amber-600" />
                </div>
                <span className="font-bold text-2xl text-[#0f2744]">10+ ans</span>
                <span className="text-gray-500 text-sm">Expérience</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-2">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <span className="font-bold text-2xl text-[#0f2744]">5000+</span>
                <span className="text-gray-500 text-sm">Clients satisfaits</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section bg-gray-50" id="services" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-10">
            <span className="badge mb-4">Nos services</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f2744] mb-4">
              Dépannage serrures Fichet
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Tous types d'interventions sur vos serrures Fichet : ouverture de porte, 
              réparation, remplacement de cylindre, dépannage après effraction.
            </p>
          </div>

          {/* Service Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service, index) => (
              <Link
                key={service.id}
                to={`/services/${service.slug}`}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all group border-2 border-transparent hover:border-orange-400"
                data-testid={`service-card-${service.slug}`}
              >
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-md">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-bold text-[#0f2744] group-hover:text-orange-600 transition-colors">
                      {service.name}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center text-orange-600 font-medium text-sm">
                    En savoir plus
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/services"
              className="btn-primary"
              data-testid="view-all-services"
            >
              Voir tous nos services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-alt" data-testid="why-us-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="badge mb-4">Pourquoi nous choisir</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0f2744] mb-6">
                Spécialistes des serrures Fichet depuis plus de 10 ans
              </h2>
              <p className="text-gray-600 mb-8">
                Notre équipe de techniciens qualifiés intervient rapidement sur tous 
                types de serrures Fichet. Devis gratuit, tarifs transparents, 
                intervention sans dégât dans 95% des cas.
              </p>

              <div className="space-y-4">
                {[
                  "Intervention en 30 minutes en moyenne",
                  "Ouverture sans dégât dans 95% des cas",
                  "Devis gratuit et transparent",
                  "Pièces de qualité avec garantie",
                  "Techniciens formés aux systèmes Fichet",
                  "Disponible 7j/7 et 24h/24"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              {config && (
                <a
                  href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                  className="btn-cta mt-8 inline-flex"
                  data-testid="why-us-cta"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {config.phone}
                </a>
              )}
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80"
                alt="Serrurier professionnel"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                    <Shield className="w-8 h-8 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0f2744]">Entreprise indépendante</p>
                    <p className="text-sm text-gray-500">Spécialiste Fichet</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="badge mb-4">Témoignages</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f2744] mb-4">
              Ils nous font confiance
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <div className="star-rating mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">
                  "{testimonial.comment}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[#0f2744]">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.city}</p>
                  </div>
                  <span className="text-xs text-gray-400">{testimonial.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zones d'intervention */}
      <section className="section-alt" data-testid="zones-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="badge mb-4">Zones d'intervention</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f2744] mb-4">
              Serrurier Fichet en Île-de-France
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Nous intervenons rapidement dans Paris et toute l'Île-de-France 
              pour tous vos problèmes de serrurerie Fichet.
            </p>
          </div>

          {/* Departments Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {departments.map((dept) => (
              <Link
                key={dept.id}
                to={`/departement/${dept.slug}`}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl hover:border-orange-400 border-2 border-transparent transition-all group"
                data-testid={`dept-${dept.slug}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    {dept.code}
                  </div>
                  <h3 className="font-bold text-[#0f2744] group-hover:text-orange-600 transition-colors">
                    {dept.name}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  {dept.cities.slice(0, 4).join(", ")}...
                </p>
                <span className="inline-flex items-center text-orange-600 text-sm font-medium">
                  Voir les détails
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-amber-50 border-y border-amber-200 py-6">
        <div className="container-custom">
          <p className="text-center text-amber-800 text-sm">
            <strong>⚠️ Entreprise indépendante</strong> spécialisée dans le dépannage de serrures Fichet. 
            Nous ne faisons pas partie du réseau officiel Point Fort Fichet.
          </p>
        </div>
      </section>

      {/* Mobile Quote Form */}
      <section className="section lg:hidden bg-gray-50">
        <div className="container-custom">
          <QuoteForm services={services} departments={departments} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-[#0f2744] to-[#1a3a5c] py-16 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Besoin d'un serrurier Fichet ?
          </h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            Intervention rapide 7j/7, devis gratuit, tarifs transparents.
            Appelez-nous maintenant !
          </p>
          {config && (
            <a
              href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-10 py-5 rounded-xl font-bold text-2xl shadow-2xl transition-all hover:scale-105"
              data-testid="final-cta"
            >
              <Phone className="w-6 h-6 mr-2" />
              {config.phone}
            </a>
          )}
        </div>
      </section>
    </div>
  );
}
